import { useCallback } from "react"
import type { Editor } from "@tiptap/react"
import { CircleCheck, FileText, Highlighter, Lightbulb, Link2 } from "lucide-react"

// --- Icons ---
import { CodeBlockIcon } from "@/components/tiptap-icons/code-block-icon"
import { HeadingOneIcon } from "@/components/tiptap-icons/heading-one-icon"
import { HeadingTwoIcon } from "@/components/tiptap-icons/heading-two-icon"
import { HeadingThreeIcon } from "@/components/tiptap-icons/heading-three-icon"
import { ImageIcon } from "@/components/tiptap-icons/image-icon"
import { ListIcon } from "@/components/tiptap-icons/list-icon"
import { ListOrderedIcon } from "@/components/tiptap-icons/list-ordered-icon"
import { BlockquoteIcon } from "@/components/tiptap-icons/blockquote-icon"
import { ListTodoIcon } from "@/components/tiptap-icons/list-todo-icon"
import { ChevronRightIcon } from "@/components/tiptap-icons/chevron-right-icon"
import { MinusIcon } from "@/components/tiptap-icons/minus-icon"
import { TypeIcon } from "@/components/tiptap-icons/type-icon"
import { AtSignIcon } from "@/components/tiptap-icons/at-sign-icon"
import { SmilePlusIcon } from "@/components/tiptap-icons/smile-plus-icon"
import { TableIcon } from "@/components/tiptap-icons/table-icon"
import { ListIndentedIcon } from "@/components/tiptap-icons/list-indented-icon"

// --- Lib ---
import { NOTION_BLOCK_SHORTCUTS } from "@/lib/notion-shortcuts"
import {
  isExtensionAvailable,
  isNodeInSchema,
} from "@/lib/tiptap-utils"

// --- Tiptap UI ---
import type { SuggestionItem } from "@/components/tiptap-ui-utils/suggestion-menu"
import { addEmojiTrigger } from "@/components/tiptap-ui/emoji-trigger-button"
import { addMentionTrigger } from "@/components/tiptap-ui/mention-trigger-button"
import { insertToggle } from "@/components/shared/notion-toggle"
import { selectCanUseMemo, selectCanUseTodo, useAppStore } from "@/store/useAppStore"

export interface SlashMenuConfig {
  enabledItems?: SlashMenuItemType[]
  customItems?: SuggestionItem[]
  itemGroups?: {
    [key in SlashMenuItemType]?: string
  }
  showGroups?: boolean
}

const texts = {
  // Style
  text: {
    title: "Text",
    subtext: "Regular text paragraph",
    keywords: ["p", "paragraph", "text"],
    badge: TypeIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.paragraph.keys,
  },
  heading_1: {
    title: "Heading 1",
    subtext: "Top-level heading",
    keywords: ["h", "heading1", "h1"],
    badge: HeadingOneIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.heading1.keys,
  },
  heading_2: {
    title: "Heading 2",
    subtext: "Key section heading",
    keywords: ["h2", "heading2", "subheading"],
    badge: HeadingTwoIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.heading2.keys,
  },
  heading_3: {
    title: "Heading 3",
    subtext: "Subsection and group heading",
    keywords: ["h3", "heading3", "subheading"],
    badge: HeadingThreeIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.heading3.keys,
  },
  bullet_list: {
    title: "Bullet List",
    subtext: "List with unordered items",
    keywords: ["ul", "li", "list", "bulletlist", "bullet list"],
    badge: ListIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.bulletList.keys,
  },
  ordered_list: {
    title: "Numbered List",
    subtext: "List with ordered items",
    keywords: ["ol", "li", "list", "numberedlist", "numbered list"],
    badge: ListOrderedIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.orderedList.keys,
  },
  task_list: {
    title: "To-do list",
    subtext: "List with tasks",
    keywords: ["tasklist", "task list", "todo", "checklist"],
    badge: ListTodoIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.taskList.keys,
  },
  quote: {
    title: "Blockquote",
    subtext: "Blockquote block",
    keywords: ["quote", "blockquote"],
    badge: BlockquoteIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.blockquote.keys,
  },
  code_block: {
    title: "Code Block",
    subtext: "Code block with syntax highlighting",
    keywords: ["code", "pre"],
    badge: CodeBlockIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.codeBlock.keys,
  },
  toggle: {
    title: "Toggle",
    subtext: "折りたたみ可能なブロック",
    keywords: ["toggle", "details", "collapse", "fold", "折りたたみ", "トグル"],
    badge: ChevronRightIcon,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.toggle.keys,
  },
  callout: {
    title: "Callout",
    subtext: "アイコンと背景色で目立たせるブロック",
    keywords: ["callout", "note", "info", "tip", "warning", "コールアウト", "注意", "補足"],
    badge: Lightbulb,
    group: "Style",
    shortcut: NOTION_BLOCK_SHORTCUTS.callout.keys,
  },

  // Insert
  mention: {
    title: "Mention",
    subtext: "Mention a user or item",
    keywords: ["mention", "user", "item", "tag"],
    badge: AtSignIcon,
    group: "Insert",
  },
  emoji: {
    title: "Emoji",
    subtext: "Insert an emoji",
    keywords: ["emoji", "emoticon", "smiley"],
    badge: SmilePlusIcon,
    group: "Insert",
  },
  table: {
    title: "Table",
    subtext: "Insert a table",
    aliases: ["table", "insertTable"],
    badge: TableIcon,
    group: "Insert",
  },
  divider: {
    title: "Separator",
    subtext: "Horizontal line to separate content",
    keywords: ["hr", "horizontalRule", "line", "separator"],
    badge: MinusIcon,
    group: "Insert",
  },
  todo_link: {
    title: "Todo",
    subtext: "Todo を作成・検索して埋め込む（チェックで完了）",
    keywords: ["todo", "task", "タスク", "やること"],
    badge: CircleCheck,
    group: "Insert",
  },
  highlight_ref: {
    title: "Highlight",
    subtext: "本のハイライトを引用する（出典からその位置を開ける）",
    keywords: ["highlight", "quote", "cite", "ハイライト", "引用", "読書"],
    badge: Highlighter,
    group: "Insert",
  },
  doc_link: {
    title: "Link to page",
    subtext: "メモや本にリンクする（[[ でも開けます）",
    keywords: ["link", "page", "wiki", "リンク", "ページ", "メモ"],
    badge: Link2,
    group: "Insert",
  },
  toc: {
    title: "Table of contents",
    subtext: "Insert a table of contents",
    keywords: ["toc", "tableofcontents", "table of contents"],
    badge: ListIndentedIcon,
    group: "Insert",
  },

  // Upload
  image: {
    title: "Image",
    subtext: "Resizable image with caption",
    keywords: [
      "image",
      "imageUpload",
      "upload",
      "img",
      "picture",
      "media",
      "url",
    ],
    badge: ImageIcon,
    group: "Upload",
  },
  pdf: {
    title: "PDF",
    subtext: "PDF を埋め込む",
    keywords: ["pdf", "file", "document", "attachment", "ファイル", "添付"],
    badge: FileText,
    group: "Upload",
  },
}

export type SlashMenuItemType = keyof typeof texts

const getItemImplementations = () => {
  return {
    // Style
    text: {
      check: (editor: Editor) => isNodeInSchema("paragraph", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setParagraph().run()
      },
    },
    heading_1: {
      check: (editor: Editor) => isNodeInSchema("heading", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 1 }).run()
      },
    },
    heading_2: {
      check: (editor: Editor) => isNodeInSchema("heading", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 2 }).run()
      },
    },
    heading_3: {
      check: (editor: Editor) => isNodeInSchema("heading", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleHeading({ level: 3 }).run()
      },
    },
    bullet_list: {
      check: (editor: Editor) => isNodeInSchema("bulletList", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleBulletList().run()
      },
    },
    ordered_list: {
      check: (editor: Editor) => isNodeInSchema("orderedList", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleOrderedList().run()
      },
    },
    task_list: {
      check: (editor: Editor) => isNodeInSchema("taskList", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleTaskList().run()
      },
    },
    quote: {
      check: (editor: Editor) => isNodeInSchema("blockquote", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleBlockquote().run()
      },
    },
    code_block: {
      check: (editor: Editor) => isNodeInSchema("codeBlock", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().toggleNode("codeBlock", "paragraph").run()
      },
    },
    toggle: {
      check: (editor: Editor) => isNodeInSchema("details", editor),
      action: ({ editor }: { editor: Editor }) => {
        insertToggle(editor)
      },
    },
    callout: {
      check: (editor: Editor) => isNodeInSchema("callout", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setCallout().run()
      },
    },

    // Insert
    mention: {
      check: (editor: Editor) =>
        isExtensionAvailable(editor, ["mention", "mentionAdvanced"]),
      action: ({ editor }: { editor: Editor }) => addMentionTrigger(editor),
    },
    emoji: {
      check: (editor: Editor) =>
        isExtensionAvailable(editor, ["emoji", "emojiPicker"]),
      action: ({ editor }: { editor: Editor }) => addEmojiTrigger(editor),
    },
    divider: {
      check: (editor: Editor) => isNodeInSchema("horizontalRule", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().setHorizontalRule().run()
      },
    },
    toc: {
      check: (editor: Editor) => isNodeInSchema("tocNode", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().insertTocNode().run()
      },
    },
    todo_link: {
      // Todo API は todo 権限（管理者は常に可）が必要
      check: (editor: Editor) =>
        isNodeInSchema("todoLink", editor) && selectCanUseTodo(useAppStore.getState()),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().insertTodoLink().run()
      },
    },
    highlight_ref: {
      check: (editor: Editor) => isNodeInSchema("highlightRef", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().insertHighlightRef().run()
      },
    },
    doc_link: {
      // リンク候補・メモ作成は memo 権限が要る（TODO メモだけ使える人には出さない）
      check: (editor: Editor) =>
        isNodeInSchema("docLink", editor) && selectCanUseMemo(useAppStore.getState()),
      // [[ を入れて候補メニューを開く
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().insertContent("[[").run()
      },
    },
    table: {
      check: (editor: Editor) => isNodeInSchema("table", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertTable({
            rows: 3,
            cols: 3,
            withHeaderRow: false,
          })
          .run()
      },
    },

    // Upload
    image: {
      check: (editor: Editor) => isNodeInSchema("image", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "imageUpload",
          })
          .run()
      },
    },
    pdf: {
      check: (editor: Editor) => isNodeInSchema("pdfEmbed", editor),
      action: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().insertPdfEmbed().run()
      },
    },
  }
}

function organizeItemsByGroups(
  items: SuggestionItem[],
  showGroups: boolean
): SuggestionItem[] {
  if (!showGroups) {
    return items.map((item) => ({ ...item, group: "" }))
  }

  const groups: { [groupLabel: string]: SuggestionItem[] } = {}

  // Group items
  items.forEach((item) => {
    const groupLabel = item.group || ""
    if (!groups[groupLabel]) {
      groups[groupLabel] = []
    }
    groups[groupLabel].push(item)
  })

  // Flatten groups in order (this maintains the visual order for keyboard navigation)
  const organizedItems: SuggestionItem[] = []
  Object.entries(groups).forEach(([, groupItems]) => {
    organizedItems.push(...groupItems)
  })

  return organizedItems
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config?: SlashMenuConfig) {
  const getSlashMenuItems = useCallback(
    (editor: Editor) => {
      const items: SuggestionItem[] = []

      const enabledItems =
        config?.enabledItems || (Object.keys(texts) as SlashMenuItemType[])
      const showGroups = config?.showGroups !== false

      const itemImplementations = getItemImplementations()

      enabledItems.forEach((itemType) => {
        const itemImpl = itemImplementations[itemType]
        const itemText = texts[itemType]

        if (itemImpl && itemText && itemImpl.check(editor)) {
          const item: SuggestionItem = {
            onSelect: ({ editor }) => itemImpl.action({ editor }),
            ...itemText,
          }

          if (config?.itemGroups?.[itemType]) {
            item.group = config.itemGroups[itemType]
          } else if (!showGroups) {
            item.group = ""
          }

          items.push(item)
        }
      })

      if (config?.customItems) {
        items.push(...config.customItems)
      }

      // Reorganize items by groups to ensure keyboard navigation works correctly
      return organizeItemsByGroups(items, showGroups)
    },
    [config]
  )

  return {
    getSlashMenuItems,
    config,
  }
}
